'use client'

/**
 * Three-tier access gate: all pages reachable; premium content only when hasAccess.
 * No redirects - shows in-page placeholder for SEO and free users.
 */

import { useSession, signIn } from 'next-auth/react'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, BarChart3, Crown, Lock, ShieldCheck, TrendingUp } from 'lucide-react'
import { PRICING } from '@/lib/pricing'
import { FREE_TIER_DAILY_MINUTES } from '@/lib/subscription-utils'
import LiveValueMoment from '@/components/LiveValueMoment'
import CaughtEarlyProof from '@/components/CaughtEarlyProof'
import { SHOW_VERIFY_PROMPT_EVENT } from '@/components/VerifyEmailPrompt'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { SUB_CACHE_KEY, SUB_CACHE_TTL_MS, clearSubscriptionCache, isPostCheckoutReturn } from '@/lib/premium/subscriptionCache'
import { useInitialAccess } from '@/components/InitialAccessProvider'
import { DelayedFeedProvider, DelayedFeedBanner } from '@/components/DelayedFeedContext'

export type PlaceholderVariant = 'alerts' | 'chart'

interface PremiumContentGateProps {
  children: React.ReactNode
  /** Variant for placeholder copy (alerts vs chart) */
  variant?: PlaceholderVariant
  /** Optional symbol for chart placeholder (e.g. "AAPL") */
  symbol?: string
}

const SESSION_LOAD_TIMEOUT_MS = 12_000
// Reduced from 8s → 4s: fail-open AbortError path grants access; shorter wait is better UX.
const SUBSCRIPTION_FETCH_TIMEOUT_MS = 4_000
// Cache subscription result for 5 minutes per browser session to avoid re-fetching on every navigation.


interface SubCache {
  hasAccess: boolean
  freeTierAllowUntil: string | null
  freeTierLimitReached: boolean
  // Card-on-file trial framing for the paywall, cached so a navigation off the fresh cache still
  // leads the out-of-time gate with the trial (register: trial-at-freetier-wall-0822).
  trialEligible: boolean
  trialDays: number
  cachedAt: number
  email: string
}

function readSubCache(email: string): SubCache | null {
  try {
    const raw = sessionStorage.getItem(SUB_CACHE_KEY)
    if (!raw) return null
    const parsed: SubCache = JSON.parse(raw)
    if (parsed.email !== email) return null
    if (Date.now() - parsed.cachedAt > SUB_CACHE_TTL_MS) return null
    return parsed
  } catch { return null }
}

function writeSubCache(email: string, data: Pick<SubCache, 'hasAccess' | 'freeTierAllowUntil' | 'freeTierLimitReached' | 'trialEligible' | 'trialDays'>) {
  try {
    const entry: SubCache = { ...data, email, cachedAt: Date.now() }
    sessionStorage.setItem(SUB_CACHE_KEY, JSON.stringify(entry))
  } catch { /* ignore */ }
}


export default function PremiumContentGate({ children, variant = 'alerts', symbol }: PremiumContentGateProps) {
  // Server-resolved flags. ⚠ NOT imported from lib/feature-flags: this is a client
  // component, those read process.env without NEXT_PUBLIC_, and importing them here
  // renders the real value on the server and the 'false' default in the browser
  // (hydration mismatch). See src/contexts/FeatureFlagsContext.tsx.
  const { freeTrialStart, subscriptionGating } = useFeatureFlags()
  const { data: session, status } = useSession()
  // ⭐ SERVER-COMPUTED VERDICT, WHEN THE SURFACE PROVIDES ONE. Without it the first paint is
  // unconditionally the spinner, because `loading` starts true and only an effect can clear it —
  // which is what made every /alerts refresh flash "Checking access..." at paying subscribers.
  // See InitialAccessProvider.tsx. When it is null (surfaces that do not provide one) every line
  // below behaves exactly as it did before.
  const initialAccess = useInitialAccess()
  const [hasAccess, setHasAccess] = useState<boolean | null>(initialAccess?.hasAccess ?? null)
  const [freeTierAllowUntil, setFreeTierAllowUntil] = useState<string | null>(initialAccess?.freeTierAllowUntil ?? null)
  const [freeTierLimitReached, setFreeTierLimitReached] = useState(initialAccess?.freeTierLimitReached ?? false)
  // Card-on-file trial framing for the paywall (register: trial-at-freetier-wall-0822). Defaults
  // false/0 so a gate never promises a trial before the server has confirmed eligibility.
  const [trialEligible, setTrialEligible] = useState(false)
  const [trialDays, setTrialDays] = useState(0)
  // Unverified user — drives the truthful "verify your email" wall/nudge instead of the false
  // "you watched 15 minutes" message (register: new-user-instant-wrong-paywall).
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false)
  // Sell time: minutes behind on the delayed free feed (0 = real time). Set only from subscription-status.
  const [delayedFeed, setDelayedFeed] = useState(0)
  // ⚠ Only skip the loading state when the server said ACCESS IS GRANTED. A server "no" still goes
  // through the normal check: a stale deny would wrongly show the paywall to someone who just
  // paid, and that exact complaint ("it asked me to pay aagain") is already in the support chat.
  const [loading, setLoading] = useState(!(initialAccess?.hasAccess === true))
  const [sessionTimedOut, setSessionTimedOut] = useState(false)

  // If session never resolves, stop blocking after timeout (avoid infinite "Checking access")
  useEffect(() => {
    if (status !== 'loading') return
    const t = setTimeout(() => setSessionTimedOut(true), SESSION_LOAD_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [status])

  useEffect(() => {
    if (status === 'loading' && !sessionTimedOut) return
    if (!session?.user && !sessionTimedOut) {
      setHasAccess(false)
      setFreeTierAllowUntil(null)
      setFreeTierLimitReached(false)
      setLoading(false)
      return
    }
    if (sessionTimedOut && !session?.user) {
      setHasAccess(false)
      setFreeTierAllowUntil(null)
      setFreeTierLimitReached(false)
      setLoading(false)
      return
    }
    // When gating is disabled, skip API call - grant access immediately (no spinner delay).
    if (!subscriptionGating) {
      setHasAccess(true)
      setFreeTierLimitReached(false)
      setLoading(false)
      return
    }

    const email = session?.user?.email ?? ''

    // 🚨 A RETURN FROM CHECKOUT MUST NEVER READ THE CACHE. The verdict changed on the server the
    // moment the webhook fulfilled (measured at 16-22s), but this tab still holds the pre-payment
    // `{hasAccess:false}` for 5 minutes — and does not even background-refresh for the first 2.5.
    // That is what put "it asked me to pay aagain" in the support chat.
    if (typeof window !== 'undefined' && isPostCheckoutReturn(window.location.search, window.location.pathname)) {
      clearSubscriptionCache()
    }

    // Use cached result if fresh — eliminates spinner on every page navigation.
    const cached = readSubCache(email)
    if (cached) {
      setHasAccess(cached.hasAccess)
      setFreeTierAllowUntil(cached.freeTierAllowUntil)
      setFreeTierLimitReached(cached.freeTierLimitReached)
      setTrialEligible(cached.trialEligible === true)
      setTrialDays(typeof cached.trialDays === 'number' ? cached.trialDays : 0)
      setNeedsEmailVerification((cached as { needsEmailVerification?: boolean }).needsEmailVerification === true)
      setDelayedFeed(Number((cached as { delayedFeed?: number }).delayedFeed) || 0)
      setLoading(false)
      // Refresh in background if older than half the TTL
      if (Date.now() - cached.cachedAt < SUB_CACHE_TTL_MS / 2) return
    }

    const check = async () => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), SUBSCRIPTION_FETCH_TIMEOUT_MS)
      try {
        const res = await fetch('/api/user/subscription-status', { signal: controller.signal })
        clearTimeout(timeoutId)
        if (res.ok) {
          const data = await res.json()
          const needsVerify = data.needsEmailVerification === true
          const result = {
            hasAccess: data.hasAccess === true,
            freeTierAllowUntil: data.freeTierAllowUntil ?? null,
            // Only a TRUE free-tier exhaustion — never the unverified state, which shares the same
            // accessLevel/hasAccess shape but must read "verify your email", not "you watched 15 min".
            freeTierLimitReached: !needsVerify && (data.freeTierLimitReached === true || (data.accessLevel === 'free_tier' && data.hasAccess === false)),
            trialEligible: data.trialEligible === true,
            trialDays: typeof data.stripeTrialDays === 'number' ? data.stripeTrialDays : 0,
            needsEmailVerification: needsVerify,
            // Sell time: minutes behind when on the delayed free feed, else 0 (field absent = real time).
            delayedFeed: data.delayedFeed === true && typeof data.delayMinutes === 'number' ? data.delayMinutes : 0,
          }
          setHasAccess(result.hasAccess)
          setDelayedFeed(result.delayedFeed)
          setFreeTierAllowUntil(result.freeTierAllowUntil)
          setFreeTierLimitReached(result.freeTierLimitReached)
          setTrialEligible(result.trialEligible)
          setTrialDays(result.trialDays)
          setNeedsEmailVerification(result.needsEmailVerification)
          writeSubCache(email, result)
        } else {
          setHasAccess(false)
          setFreeTierAllowUntil(null)
          setFreeTierLimitReached(false)
        }
      } catch (err) {
        clearTimeout(timeoutId)
        // Timeout or network error: fail open when user is signed in so they aren't blocked
        if (err instanceof Error && err.name === 'AbortError') {
          setHasAccess(true)
          setFreeTierLimitReached(false)
        } else {
          setHasAccess(false)
          setFreeTierAllowUntil(null)
          setFreeTierLimitReached(false)
        }
      } finally {
        setLoading(false)
      }
    }
    check()
  }, [session, status, sessionTimedOut])

  // ⚠ `status === 'loading'` must not spin either when the server already granted access, or the
  // flash simply moves from one condition to the other and the fix reads as if it did nothing.
  const serverGranted = initialAccess?.hasAccess === true
  // Signed out per the server AND no client session: the answer is already known, so paint the sign-in card
  // now. The spinner here pushed the SEO section below the fold, which moved /alerts LCP from the 2.9s first
  // paint to the ~4s card (mobile, 2026-10-31, register alerts-cls-gate-and-hero-1030). The stale-deny risk
  // the spinner guards against (someone who just paid) only exists for a SIGNED-IN user, so it does not apply.
  // Keyed on !session, not on status, so the frame between "unauthenticated" and the effect clearing
  // `loading` cannot flash the spinner either. A session that appears falls through to the normal check.
  if (initialAccess?.signedIn === false && !session?.user) {
    return <Placeholder variant={variant} kind="signin" symbol={symbol} />
  }
  if (!serverGranted && ((status === 'loading' && !sessionTimedOut) || loading)) {
    // Same min-height as the Placeholder card this resolves into (both gate variants, alerts and chart, are
    // full-page). It was min-h-[50vh], so on resolve the box grew ~360px and shoved the server-rendered SEO
    // section below it off-screen: 0.18 of the 0.43 CLS a signed-out phone measured on /alerts 2026-10-30
    // (register alerts-cls-gate-and-hero-1030).
    return (
      <div className="min-h-[calc(100dvh-4rem)] flex items-center justify-center px-4">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card/50 px-5 py-4 text-foreground/80">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-600 border-t-amber-600 dark:border-t-amber-400" />
          <span className="text-sm font-medium">Checking access...</span>
        </div>
      </div>
    )
  }

  if (!session?.user) {
    return <Placeholder variant={variant} kind="signin" symbol={symbol} />
  }

  // SELL TIME (sell-time-delay-model-scope-1004): a delayed free-tier user sees the FEED, all day, behind by
  // `delayMinutes`, which REPLACES the daily-limit wall on /alerts. Checked before the walls, and regardless of
  // hasAccess (the server already holds every alert back for them on every channel). Alerts surface only:
  // other premium surfaces keep their existing gate.
  const onDelayedFeed = variant === 'alerts' && delayedFeed > 0
  const feed = (
    <DelayedFeedProvider minutes={onDelayedFeed ? delayedFeed : 0}>
      {onDelayedFeed && <DelayedFeedBanner minutes={delayedFeed} />}
      {children}
    </DelayedFeedProvider>
  )
  if (onDelayedFeed) return feed

  const limitReached = freeTierLimitReached === true
  // Unverified user who has genuinely run out of access → truthful "verify your email" wall, never the
  // false "you watched 15 minutes" one (register: new-user-instant-wrong-paywall).
  if (!hasAccess && needsEmailVerification) {
    return <Placeholder variant={variant} kind="verify_email" symbol={symbol} trialEligible={trialEligible} trialDays={trialDays} />
  }
  if (!hasAccess && limitReached) {
    return <Placeholder variant={variant} kind="free_tier_limit" symbol={symbol} trialEligible={trialEligible} trialDays={trialDays} />
  }

  if (!hasAccess) {
    return <Placeholder variant={variant} kind="subscribe" symbol={symbol} trialEligible={trialEligible} trialDays={trialDays} />
  }

  // FreeTierCountdown removed: component was never implemented, caused "Can't find variable: FreeTierCountdown" error.
  // Server enforces free-tier limit on next API call; no client countdown needed.
  // Same wrapper as the delayed path (provides null here), so the tree never changes shape.
  return feed
}

function Placeholder({
  variant,
  kind,
  symbol,
  trialEligible = false,
  trialDays = 0,
}: {
  variant: PlaceholderVariant
  kind: 'signin' | 'subscribe' | 'free_tier_limit' | 'verify_email'
  symbol?: string
  /** Server-confirmed card-on-file trial eligibility. When true, the out-of-time gates lead with the
   *  trial ("$0 today, cancel anytime") instead of a cold "Subscribe". Never true unless the server
   *  said so, so a churned ex-subscriber is not promised a trial. Register: trial-at-freetier-wall-0822. */
  trialEligible?: boolean
  trialDays?: number
}) {
  // The trial CTA only leads when the server confirmed eligibility AND a length is configured.
  const showTrial = trialEligible && trialDays > 0
  // Same reason as the parent: this is inside a 'use client' module, so the flag comes from the
  // server-fed provider, never from a direct lib/feature-flags import.
  const { freeTrialStart, emailVerification } = useFeatureFlags()
  const isChart = variant === 'chart'
  const title = isChart
    ? symbol
      ? `Chart: ${symbol}`
      : 'Stock chart'
    : 'Live Trading Dashboard'
  /**
   * 🚨 THIS PROMISED FIVE DAYS AND THE ACCOUNT GOT ONE (register: `signup-promises-5-delivers-1`).
   *
   * signupGrants.ts is the authority and it grants TWO things: a WELCOME DAY (1 day, on first authed
   * access, verified or not) and a TRIAL BLOCK (5 days, ONLY once the email is verified). So while
   * EMAIL_VERIFICATION_ENABLED is on, register gives 1 day and verify gives 6 total.
   *
   * ⛔ THE GAP IS NOT COSMETIC. 54% of signups never verify, so most people who read "plus 5 free
   * premium days" here received one, burned it, and hit a wall ([[signup-verify-leak-0815]]). Worse,
   * the sentence never mentioned that VERIFYING is the thing that unlocks the five, at precisely the
   * moment someone is deciding to sign up. The signup form itself already says this correctly
   * (app/auth/page.tsx:579); this gate, the first thing a signed-out visitor to /alerts sees, did not.
   *
   * ⚠ THE FLAG COMES FROM THE SERVER-FED PROVIDER, never a direct lib/feature-flags import. This is
   * a 'use client' module and EMAIL_VERIFICATION_ENABLED has no NEXT_PUBLIC_ prefix, so importing it
   * reads true during SSR and undefined on hydration - the React #425 text mismatch that HeroSection
   * documents in detail. `emailVerification` was already in the provider; it just was not read here.
   */
  const description =
    kind === 'signin'
      ? emailVerification
        ? `Create a free account to unlock live alerts, charts, and real-time data. You get a full day of premium right away, and verifying your email adds ${PRICING.trialTradingDays} more. No card needed.`
        : `Create a free account to unlock live alerts, charts, and real-time data - plus ${PRICING.trialTradingDays} free premium days. No card needed.`
      : kind === 'verify_email'
        ? `Verify your email to unlock ${PRICING.trialTradingDays} more free premium days and keep watching. Check your inbox for the link, it only takes a second.`
      : kind === 'free_tier_limit'
        ? showTrial
          ? `You just watched the live scanner for ${FREE_TIER_DAILY_MINUTES} minutes, and it has not stopped moving. Start a ${trialDays}-day free trial, $0 today, to keep watching without the timer. Cancel anytime.`
          : `You just watched the live scanner for ${FREE_TIER_DAILY_MINUTES} minutes. It doesn't stop when your preview does - subscribe to keep watching, or come back tomorrow for another ${FREE_TIER_DAILY_MINUTES}.`
        : showTrial
          ? `Start a ${trialDays}-day free trial, $0 today, to unlock the full dashboard: real-time alerts, the full scanner and live charts. Cancel anytime.`
          : !freeTrialStart
            ? 'Subscribe for full access.'
            : `Subscribe or claim your ${PRICING.trialTradingDays} free premium days to unlock the full dashboard.`

  return (
    // ⚠ MEASURED ON THE LIVE PAGE, BOTH SURFACES. DO NOT "SIMPLIFY" WITHOUT RE-MEASURING.
    //
    // This card has been off-centre in both directions in one day, so here is the actual data.
    // At 1440px with the chat dock open, on BOTH routes this gate renders on:
    //
    //     /alerts     main margin-left 0px, padding-right 0px, chat dock at 1072..1432
    //     /chart/YJ   main margin-left 0px, padding-right 0px, chat dock at 1072..1432
    //
    // <main> reserves NOTHING on either. ConditionalMain skips its chat padding on /alerts by
    // design, and on /chart it comes out 0 too — so a plain in-flow `justify-center` centres on the
    // whole viewport and lands ~184px right of the visible band. Compensating for the dock ALONE
    // then over-corrects the other way the moment the left pane is present (signed-in users have the
    // watchlist pane at 368px, which is exactly the "left of center" the owner reported).
    //
    // ⛔ position: fixed with left/right from these vars was tried and measured too. It does not
    // work: an ancestor of <main> establishes a containing block, so a fixed descendant resolves
    // left/right against <main>'s box rather than the viewport, applying the vars on top of an
    // offset that already contains them. With both vars reading 0px the element still measured
    // 368..1072 on a 1440 viewport.
    //
    // So reserve BOTH edges here, from the two live variables: --sidebar-width (ConditionalMain
    // writes the real pane width, 72 collapsed / 368 expanded) and --alerts-chat-right-pad (the
    // dock writes it on documentElement, 0 when closed). Collapse the sidebar or close the chat and
    // the card re-centres on its own, which is the owner's actual requirement: "reactive if we close
    // either of these the left pane or the chat it should adjust to center everytime".
    // The +1rem keeps the px-4 gutter the inline padding-right would otherwise overwrite.
    <div
      className="flex min-h-[calc(100dvh-4rem)] items-center justify-center bg-background px-4 py-16 text-foreground transition-[margin-left,padding-right] duration-200 ease-out"
      style={{ marginLeft: 'var(--sidebar-width, 0px)', paddingRight: 'calc(var(--alerts-chat-right-pad, 0px) + 1rem)' }}
    >
      <div className="fixed inset-0 -z-10 dark:bg-[radial-gradient(1200px_600px_at_15%_-10%,rgba(63,63,70,0.25),transparent_60%)]" />
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card shadow-2xl dark:shadow-black/50 backdrop-blur">
          <div className="h-1.5 w-full rounded-t-2xl bg-gradient-to-r from-fuchsia-500/70 via-amber-400/70 to-sky-400/70" />
          <div className="p-6 sm:p-7">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-amber-300 bg-amber-50 dark:border-amber-400/25 dark:bg-amber-400/10">
                {isChart ? (
                  <TrendingUp className="h-6 w-6 text-amber-600 dark:text-amber-300" />
                ) : (
                  <BarChart3 className="h-6 w-6 text-amber-600 dark:text-amber-300" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-[11px] font-semibold text-foreground/80">
                  <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
                  {kind === 'signin'
                    ? 'Free account required'
                    : kind === 'verify_email'
                      ? 'Verify your email'
                      : kind === 'free_tier_limit'
                        ? 'Daily limit reached'
                        : 'Pro access required'}
                </div>
                <h2 className="mt-3 text-xl font-extrabold tracking-tight text-foreground">{title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
                {kind === 'free_tier_limit' && <LiveValueMoment />}
              </div>
            </div>
            <div className="mt-5 space-y-2 rounded-xl border border-border bg-card/30 p-4 text-sm text-foreground/80">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-700 dark:text-emerald-300 shrink-0" />
                <span>Live alerts, rankings, and premium tools</span>
              </div>
              {showTrial && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-300 shrink-0" />
                  <span>{trialDays}-day free trial, $0 today, cancel anytime</span>
                </div>
              )}
              {!showTrial && kind === 'subscribe' && freeTrialStart && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-300 shrink-0" />
                  <span>{PRICING.trialTradingDays} free premium days - no card required</span>
                </div>
              )}
              {kind === 'subscribe' && !freeTrialStart && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-300 shrink-0" />
                  <span>Subscribe for full access now</span>
                </div>
              )}
            </div>
            {/* Proof at the decision moment: the scanner's real timestamped catches (register:
                proof-at-decision-moments-0822). Not for the sign-in card (that is a signup moment,
                not a paywall). Degrades to nothing when there are no receipts. */}
            {kind !== 'signin' && <CaughtEarlyProof limit={3} />}
            <div className="mt-6 space-y-3">
              {kind === 'signin' ? (
                <>
                  {/* One-click Google sign-up — biggest conversion lever. Route to /welcome (the guided
                      onboarding), NOT back to this page: every other signup path on the site lands new
                      users on /welcome, and it is the biggest lever on trial→paid. Sending Google signups
                      from the money page back to /alerts was the ONE path that skipped activation
                      (register: paywall-google-skips-welcome-onboarding). */}
                  <button
                    type="button"
                    onClick={() => signIn('google', { callbackUrl: '/welcome' })}
                    className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-zinc-900 shadow-sm transition hover:bg-zinc-100"
                  >
                    <GoogleG /> Continue with Google
                  </button>
                  <Link prefetch={false}
                    href="/auth?mode=signup"
                    className="group flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card/40 px-5 py-3 text-sm font-semibold text-foreground/80 transition hover:bg-accent dark:hover:bg-zinc-900/60"
                  >
                    Sign up free with email
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <Link prefetch={false} href="/auth" className="block w-full px-5 py-1 text-center text-xs font-semibold text-muted-foreground transition hover:text-foreground/80">
                    I already have an account
                  </Link>
                </>
              ) : kind === 'verify_email' ? (
                // Verify wall: lead with the ACTION, not "subscribe". Opens the rich global verify
                // prompt (resend + inbox help) rather than duplicating that logic here.
                <button
                  type="button"
                  onClick={() => { try { window.dispatchEvent(new Event(SHOW_VERIFY_PROMPT_EVENT)) } catch { /* noop */ } }}
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-400 to-amber-300 px-5 py-3 text-sm font-extrabold text-black shadow-lg shadow-amber-500/10 transition hover:from-amber-300 hover:via-amber-300 hover:to-amber-200"
                >
                  Verify my email
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              ) : showTrial ? (
                // The out-of-time gates lead with the card-on-file trial: the lowest-friction, highest
                // -converting ask, and the sharpest moment (a consistent user cut off mid-use).
                <Link
                  href="/subscribe?from=gate"
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-400 to-amber-300 px-5 py-3 text-sm font-extrabold text-black shadow-lg shadow-amber-500/10 transition hover:from-amber-300 hover:via-amber-300 hover:to-amber-200"
                >
                  Start my {trialDays}-day free trial, $0 today
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              ) : kind === 'free_tier_limit' || !freeTrialStart ? (
                <Link
                  href="/subscribe"
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-400 to-amber-300 px-5 py-3 text-sm font-extrabold text-black shadow-lg shadow-amber-500/10 transition hover:from-amber-300 hover:via-amber-300 hover:to-amber-200"
                >
                  Subscribe for full access
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              ) : (
                <Link
                  href="/subscribe"
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-400 to-amber-300 px-5 py-3 text-sm font-extrabold text-black shadow-lg shadow-amber-500/10 transition hover:from-amber-300 hover:via-amber-300 hover:to-amber-200"
                >
                  Claim {PRICING.trialTradingDays} free premium days
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
              <Link
                href="/"
                className="block w-full rounded-xl border border-border bg-card/40 px-5 py-3 text-center text-sm font-semibold text-foreground/80 transition hover:bg-accent dark:hover:bg-zinc-900/60"
              >
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GoogleG() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  )
}
